import { CaseContent, CaseStatus, clientApi } from "@/store/clientApi";
import { DataObject } from "@/typed/CaseStateData";
import { getFileTypeFromS3URL } from "@/utils/getFileType";
import { checkDataInIndexedDB, getDataFromIndexedDB, saveDataToIndexedDB, Store } from "@/utils/indexeddb";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosResponse, GenericAbortSignal } from "axios";

// Helper function to fetch model data
const fetchModel = async (presignedUrl: string, signal?: GenericAbortSignal) => {
  return new Promise<ArrayBuffer>((resolve, reject) => {
    axios
      .get(presignedUrl, {
        responseType: "arraybuffer",
        signal
      })
      .then((response: AxiosResponse<ArrayBuffer>) => {
        const caseData = response.data;
        resolve(caseData);
      })
      .catch((error: Error) => {
        reject(error.message);
      });
  });
};

// Helper function to fetch multiple models data with their average download progress
const fetchModelsWithProgress = async (dispatch: any, presignedUrls: string[], id: string, store: Store) => {
  return new Promise<ArrayBuffer[]>((resolve) => {
    const totalModels = presignedUrls.length;
    let downloadedModels = 0;
    const modelData: ArrayBuffer[] = new Array(totalModels);

    // Object to store the progress for each presignedUrl
    const downloadProgress: { [key: string]: number } = {};

    const fetchPromises = presignedUrls.map((url, index) => {
      return axios
        .get(url, {
          responseType: "arraybuffer",
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.total !== undefined) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              // Update progress for the specific presignedUrl
              downloadProgress[url] = progress;

              // Calculate the average progress from the downloadProgress object
              const averageProgress = Math.round(
                Object.values(downloadProgress).reduce((sum, progress) => sum + progress, 0) / totalModels
              );

              // Dispatch the average progress
              dispatch(viewportSlice.actions.setModelDownloadProgress({ progress: averageProgress, id, store }));
            }
          }
        })
        .then((response: AxiosResponse<ArrayBuffer>) => {
          modelData[index] = response.data;
          downloadedModels += 1;
          // Once all models are downloaded, resolve the promise
          if (downloadedModels === totalModels) {
            resolve(modelData);
          }
        });
    });

    // Wait for all fetch requests to complete
    Promise.allSettled(fetchPromises).then(() => {
      resolve(modelData);
    });
  });
};

interface ViewportModelData {
  modelDownloadProgress: number | undefined;
  modelViewportError: string | undefined;
  loading: boolean;
  loaded: boolean;
}

interface ViewportModelState {
  data: {
    [index: string]: {
      [index in Store]?: ViewportModelData;
    };
  };
}

const initialState: ViewportModelState = { data: {} };

// Slice definition
const viewportSlice = createSlice({
  name: "viewport",
  initialState,
  reducers: {
    clearModel: (state, action: PayloadAction<{ id: string; store: Store }>) => {
      if (!state.data[action.payload.id]?.[action.payload.store]) {
        return;
      }
      state.data[action.payload.id][action.payload.store] = {
        modelDownloadProgress: undefined,
        modelViewportError: undefined,
        loading: false,
        loaded: false
      };
    },
    setModelDownloadProgress: (state, action: PayloadAction<{ progress: number; id: string; store: Store }>) => {
      if (!state.data[action.payload.id]) {
        state.data[action.payload.id] = {};
      }
      state.data[action.payload.id][action.payload.store] = {
        modelDownloadProgress: action.payload.progress,
        modelViewportError: undefined,
        loading: true,
        loaded: false
      };
    },
    setModelError: (state, action: PayloadAction<{ error: string; id: string; store: Store }>) => {
      if (!state.data[action.payload.id]) {
        state.data[action.payload.id] = {};
      }
      state.data[action.payload.id][action.payload.store] = {
        modelDownloadProgress: undefined,
        modelViewportError: action.payload.error,
        loading: false,
        loaded: false
      };
    },
    setModelLoaded(state, action: PayloadAction<{ id: string; loaded: boolean; store: Store }>) {
      if (!state.data[action.payload.id]) {
        state.data[action.payload.id] = {};
      }
      state.data[action.payload.id][action.payload.store] = {
        modelViewportError: state.data[action.payload.id][action.payload.store]?.modelViewportError,
        modelDownloadProgress: action.payload.loaded
          ? 0
          : (state.data[action.payload.id][action.payload.store]?.modelDownloadProgress ?? 0),
        loading: !action.payload.loaded,
        loaded: action.payload.loaded
      };
    }
  },
  selectors: {
    selectModelDownloadProgress: (state, id: string, store: Store): number | undefined => {
      return state.data[id]?.[store]?.modelDownloadProgress;
    },
    selectModelDownloadError: (state, id: string, store: Store): string | undefined => {
      return state.data[id]?.[store]?.modelViewportError;
    },
    selectModelDownloadLoading: (state, id: string, store: Store): boolean | undefined => {
      return state.data[id]?.[store]?.loading;
    },
    selectModelLoaded(state, id: string, store: Store): boolean | undefined {
      return state.data[id]?.[store]?.loaded;
    }
  }
});

export const availableViewportModel = createAsyncThunk<
  boolean, // return type
  {
    caseId: number;
    caseStatus?: CaseStatus;
    store: Store;
    content: CaseContent;
  },
  { rejectValue: string }
>("viewportModel/fetchCase", async (params, { dispatch, getState }) => {
  const { caseId, caseStatus, store, content } = params;

  const id = caseId.toString();

  const LOWER = "lower";
  const UPPER = "upper";
  const INPUT = "input";
  const OUTPUT = "output";
  const COMBINED = "combined";

  // Check for invalid store
  if (store !== Store.ModelOutputStore && store !== Store.ScanStore) {
    dispatch(viewportSlice.actions.setModelError({ error: "Must be viewing a model", id, store }));
    return false;
  }

  // Check for invalid IDs
  if (caseId === undefined) {
    return false;
  }

  if (caseId !== undefined && caseStatus === undefined) {
    dispatch(viewportSlice.actions.setModelError({ error: "Retrieving a case requires a case status", id, store }));
    return false;
  }

  // Check the model is not already in progress
  let data = (getState() as { viewport: ViewportModelState }).viewport.data;
  if (data[id]?.[store]?.modelDownloadProgress !== undefined && data[id]?.[store]?.loading) {
    return false;
  }

  // Check the model hasn't already loaded
  if (data[id]?.[store]?.loaded && (await checkDataInIndexedDB(caseId, store))) {
    return true;
  }

  // Recheck the model isn't already in progress after await
  data = (getState() as { viewport: ViewportModelState }).viewport.data;
  if (data[id]?.[store]?.modelDownloadProgress !== undefined && data[id]?.[store]?.loading) {
    return false;
  }

  // Mark model as loading
  dispatch(viewportSlice.actions.setModelDownloadProgress({ progress: 0, id, store }));

  try {
    const models = await getDataFromIndexedDB(id, store);
    const hasLower = models?.data.some((entry) => entry.label === "lower");
    const hasUpper = models?.data.some((entry) => entry.label === "upper");
    // if 'combined': make sure with have both the upper and lower
    if (content === COMBINED && hasLower && hasUpper) {
      dispatch(viewportSlice.actions.setModelLoaded({ id, loaded: true, store }));
      return true;
    } else if (models && models.data.some((entry) => entry.label === content) && content !== COMBINED) {
      dispatch(viewportSlice.actions.setModelLoaded({ id, loaded: true, store }));
      return true;
    } else {
      const presignedUrls = [];

      const downloadRequest = dispatch(
        clientApi.endpoints.downloadCaseApiV1CasesCaseIdUploadedGet.initiate(
          {
            caseId: caseId as number
          },
          { forceRefetch: true } // avoid using cached response as presignedURLs expire
        )
      );
      const { data: downloadData } = await downloadRequest;
      // Removing the cache subscription
      downloadRequest.unsubscribe();

      if (downloadData === undefined) {
        dispatch(viewportSlice.actions.setModelError({ error: "Failed to fetch model data", id, store }));
        return false;
      }
      const dataType = store === Store.ModelOutputStore ? OUTPUT : INPUT;
      if (dataType === INPUT) {
        if (content === LOWER || content === COMBINED) {
          presignedUrls.push(downloadData.lower_scan_presigned_url);
        }
        if (content === UPPER || content === COMBINED) {
          presignedUrls.push(downloadData.upper_scan_presigned_url);
        }
      } else if (dataType === OUTPUT) {
        if (content === LOWER || content === COMBINED) {
          presignedUrls.push(downloadData.lower_model_presigned_url);
        }
        if (content === UPPER || content === COMBINED) {
          presignedUrls.push(downloadData.upper_model_presigned_url);
        }
      }

      if (presignedUrls.length === 0) {
        dispatch(viewportSlice.actions.setModelError({ error: "Failed to fetch model data", id, store }));
        return false;
      }

      const caseData = await fetchModelsWithProgress(
        dispatch,
        presignedUrls.filter((url) => url !== undefined),
        id,
        store
      );

      const dataObjects: DataObject[] = [];
      if (content === COMBINED) {
        if (caseData.length === 0) {
          dispatch(viewportSlice.actions.setModelError({ error: "Unable to fetch combined model data", id, store }));
          return false;
        }

        if (caseData[0]) {
          const lowerObj: DataObject = {
            data: caseData[0],
            label: LOWER,
            type: getFileTypeFromS3URL(presignedUrls[0]) ?? "stl"
          };
          dataObjects.push(lowerObj);
        }

        if (caseData[1]) {
          const upperObj: DataObject = {
            data: caseData[1],
            label: UPPER,
            type: getFileTypeFromS3URL(presignedUrls[1]) ?? "stl"
          };
          dataObjects.push(upperObj);
        }
      } else {
        if (caseData.length < 1) {
          dispatch(viewportSlice.actions.setModelError({ error: "Unable to fetch model data", id, store }));
          return false;
        }
        const dataObject: DataObject = {
          data: caseData[0],
          label: content,
          type: getFileTypeFromS3URL(presignedUrls[0]) ?? "stl"
        };
        dataObjects.push(dataObject);
      }
      await saveDataToIndexedDB(id, dataObjects, store);
    }
  } catch (error) { //eslint-disable-line
    dispatch(viewportSlice.actions.setModelError({ error: "Failed to fetch model data", id, store }));
    return false;
  }

  dispatch(viewportSlice.actions.setModelLoaded({ id, loaded: true, store }));
  return true;
});

export { fetchModel };
export const { clearModel, setModelError } = viewportSlice.actions;
export const { selectModelDownloadProgress, selectModelDownloadError, selectModelLoaded } = viewportSlice.selectors;
export default viewportSlice.reducer;
